import { useState } from "react";
import { MapPin, CheckCircle, WarningCircle, Crosshair } from "@phosphor-icons/react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import client from "../lib/api";
import { toast } from "sonner";

const TIER_MSG = {
  ontime: "Checked in on time",
  early: "Checked in early — bonus points!",
  early_max: "Checked in 15+ min early — max points!",
  late: "Checked in (after start — no on-time points)",
  override: "Manual override check-in (flagged for review)",
};

// On-site check-in for assigned crew. Flips the shoot to In Progress.
export const CheckInButton = ({ p, user, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [override, setOverride] = useState(null); // {distance_m, radius_m, reason}

  const mySlots = [];
  if (p.photographer_id === user.id) mySlots.push("photographer");
  if (p.pilot_id === user.id) mySlots.push("pilot");
  if (mySlots.length === 0) return null;
  if (p.status === "completed" || p.status === "cancelled") return null;

  const done = mySlots
    .map((s) => (s === "photographer" ? p.checkin_photographer : p.checkin_pilot))
    .find((c) => c && c.by === user.id);

  const submit = (lat, lon, ovr) =>
    client.post(`/properties/${p.id}/checkin`, { lat, lon, override: !!ovr });

  const finish = (res) => {
    const t = res.data.tier;
    if (t === "override") toast.warning(TIER_MSG.override);
    else toast.success(TIER_MSG[t] || "Checked in");
    setOverride(null);
    onChanged?.(res.data.shoot);
  };

  const doCheckIn = () => {
    setBusy(true);
    if (!navigator.geolocation) {
      setBusy(false);
      setOverride({ reason: "Your device can't share its location." });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const res = await submit(pos.coords.latitude, pos.coords.longitude, false);
          finish(res);
        } catch (e) {
          const d = e?.response?.data?.detail;
          if (e?.response?.status === 400 && d?.can_override) {
            setOverride({ distance_m: d.distance_m, radius_m: d.radius_m, reason: d.message });
          } else {
            toast.error(typeof d === "string" ? d : "Couldn't check in. Try again.");
          }
        } finally { setBusy(false); }
      },
      () => {
        setBusy(false);
        setOverride({ reason: "Location access was blocked. Enable it, or submit a manual override." });
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const doOverride = async () => {
    setBusy(true);
    try {
      // Re-send with whatever coords we can (may be none) + override flag.
      const res = await submit(null, null, true);
      finish(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't submit override.");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div data-testid="checkin-done" className="flex items-center gap-2 rounded-[6px] border border-[#2E4F3B]/25 bg-[#2E4F3B]/8 px-3.5 py-2.5">
        <CheckCircle size={18} weight="fill" className="text-[#2E4F3B] shrink-0" />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-[#2E4F3B]">On-site — checked in</p>
          <p className="text-xs text-[#5C5A58]">{TIER_MSG[done.tier] || "Checked in"} · {new Date(done.at).toLocaleString([], { hour: "numeric", minute: "2-digit", month: "short", day: "numeric" })}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Button data-testid="checkin-btn" onClick={doCheckIn} disabled={busy}
        className="w-full bg-[#4A0000] hover:bg-[#5C1A1A] text-white rounded-[6px]">
        <Crosshair size={17} weight="bold" className="mr-2" />{busy ? "Locating…" : "Check in on-site"}
      </Button>
      <p className="text-[11px] text-[#9A968E] mt-1.5 flex items-center gap-1"><MapPin size={12} weight="fill" />You must be at the property. Checking in marks the shoot “In Progress”.</p>

      <Dialog open={!!override} onOpenChange={(o) => !o && !busy && setOverride(null)}>
        <DialogContent data-testid="checkin-override-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2"><WarningCircle size={22} weight="fill" className="text-[#B08D57]" />Not at the property?</DialogTitle>
            <DialogDescription>
              {override?.reason || "We couldn't confirm you're on-site."}
              {override?.distance_m != null && <> You appear to be about <b>{override.distance_m.toLocaleString()} m</b> away (need to be within {override.radius_m} m).</>}
              <br /><br />If you're genuinely on-site, you can submit a <b>manual override</b>. It still marks the shoot In Progress but earns <b>no on-time points</b> and is <b>flagged to admins</b> — frequent overrides may lead to strikes.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" data-testid="checkin-override-cancel" disabled={busy} onClick={() => setOverride(null)}>Cancel</Button>
            <Button data-testid="checkin-override-confirm" disabled={busy} onClick={doOverride} className="bg-[#B08D57] hover:bg-[#9a7a49] text-white">{busy ? "Submitting…" : "Check in anyway (override)"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
